import { Alert, Loader, Progress } from '@mantine/core'
import { useCheckRole } from '@ospace/auth'
import { CampaignLayout } from '@ospace/campaign'
import { userTypes } from '@ospace/core-auth'
import { PageTitle } from '@ospace/layout'
import { ErrorBoundary } from '@ospace/shared'
import { IconCheck, IconX } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { ContactErrorsTable } from '../components/ContactErrorsTable'
import { useContactUpload } from '../hooks/useContactUpload'
import { ContactUploadStatus, useContactUploadStatus } from '../hooks/useContactUploadStatus'

type Props = {
  importId: number
  campaignId: number
}

const DEFAULT_INTERVAL_SECONDS = 10
const MAX_WAIT_INTERVAL = 50

const getStatusMessage = (status: ContactUploadStatus) => {
  if (status.status === 'SUCCEEDED') {
    return (
      <Alert variant='light' color='green' title='Contacts processed successfully'>
        All {status.importSize} contacts have been processed successfully. You can now view them in
        the contacts page tomorrow.
      </Alert>
    )
  } else if (['QUEUED', 'PREPARED'].includes(status.status)) {
    return (
      <Alert variant='light' color='yellow' title='Queued'>
        Queued for processing. Please wait for a few minutes.
      </Alert>
    )
  } else if (status.status === 'IN PROGRESS') {
    const progress = Math.round((status.successCount / status.importSize) * 100)
    return (
      <div>
        <Progress.Root size='2xl'>
          <Progress.Section value={progress} color='blue'>
            <Progress.Label>{progress}%</Progress.Label>
          </Progress.Section>
        </Progress.Root>
        <div style={{ paddingTop: '1.5rem' }}>
          Successfully processed : {status.successCount} / {status.importSize}
        </div>
      </div>
    )
  } else {
    return (
      <>
        <Alert
          variant='light'
          color='red'
          title={`Contacts processing ${status.status}. Total contacts: ${status.importSize}`}
        >
          <div style={{ display: 'flex', gap: '1rem' }}>
            <span>
              <IconCheck color='green' /> Successful contacts: {status.successCount}
            </span>
            <span>
              <IconX color='red' /> Failed contacts: {status.failedUploads.length}
            </span>
          </div>
        </Alert>
        {status.failedUploads.length > 0 && (
          <ContactErrorsTable contactErrors={status.failedUploads} />
        )}
      </>
    )
  }
}

export const ContactUploadStatusPage = ({ importId }: Props) => {
  const [intervalMs, setIntervalMs] = useState<number | false>(DEFAULT_INTERVAL_SECONDS * 1000)
  const [refetchingCounter, SetRefetchingCounter] = useState<number>(0)
  const { isLoading, isRefetching, data: status } = useContactUploadStatus(importId, intervalMs)
  const contactUpload = useContactUpload(importId)

  useEffect(() => {
    if (status && status?.importSize > 0) {
      SetRefetchingCounter(0)
    }
    if ((status?.terminated && status.importSize > 0) || refetchingCounter > MAX_WAIT_INTERVAL) {
      setIntervalMs(false)
    }
  }, [isLoading, refetchingCounter, status])

  useEffect(() => {
    if (!contactUpload?.isLoading && !contactUpload?.data?.id) {
      setIntervalMs(false)
    }
  }, [contactUpload])

  useEffect(() => {
    if (isRefetching) {
      SetRefetchingCounter((r) => r + 1)
    }
  }, [isRefetching])

  return (
    <CampaignLayout>
      <PageTitle>Upload Contacts Status</PageTitle>
      <div className='card' style={{ minHeight: 200 }}>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>
              Hubspot Contacts Upload: {contactUpload?.data?.fileName}
            </span>
          </h3>
        </div>
        <div
          style={{
            padding: '0 2.25rem',
            paddingTop: '1.5rem',
          }}
        >
          {!contactUpload?.isLoading && !contactUpload?.data?.id ? (
            <div>Import process #{importId} does not exist.</div>
          ) : isLoading || !status?.status ? (
            <Loader color='blue' />
          ) : (
            getStatusMessage(status)
          )}
        </div>
      </div>
    </CampaignLayout>
  )
}

const Controller = () => {
  useCheckRole([userTypes.Admin, userTypes.ClientManager, userTypes.CampaignManager])
  const params: any = useParams()
  return <ContactUploadStatusPage importId={params.importId} campaignId={params.id} />
}

export const ContactUploadStatusPageRouteHandler = () => (
  <ErrorBoundary>
    <Controller />
  </ErrorBoundary>
)
